/**
 * ChatInput 组件
 * 统一的聊天输入框组件，支持 AgentFlow 和 MemoryService 两种风格
 * v6.0: 支持语音输入按钮
 */

import { forwardRef, useRef, useEffect } from 'react'
import { Send, Loader2, Mic } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { ChatVariant, ChatTheme } from './types'

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  variant?: ChatVariant
  theme?: ChatTheme
  autoFocus?: boolean
  // v6.0: 语音输入支持
  showVoiceButton?: boolean  // 是否显示麦克风按钮
  voiceMode?: 'off' | 'ptt' | 'vad' | 'vad_interrupt'  // 当前语音模式
  isRecording?: boolean  // 是否正在录音
  onVoiceClick?: () => void  // 点击麦克风按钮
  onVoiceLongPressStart?: (e: React.MouseEvent | React.TouchEvent) => void  // 长按麦克风开始
  onVoiceLongPressEnd?: () => void  // 长按麦克风结束
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(({
  value,
  onChange,
  onSend,
  placeholder = '输入消息...',
  disabled = false,
  isLoading = false,
  variant = 'agent',
  theme = 'light',
  autoFocus = false,
  // v6.0: 语音输入
  showVoiceButton = false,
  voiceMode = 'off',
  isRecording = false,
  onVoiceClick,
  onVoiceLongPressStart,
  onVoiceLongPressEnd,
}, ref) => {
  const internalRef = useRef<HTMLInputElement>(null)
  
  // 合并外部 ref 和内部 ref
  const setRefs = (element: HTMLInputElement | null) => {
    // 设置内部 ref
    (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = element
    
    // 转发给外部 ref
    if (typeof ref === 'function') {
      ref(element)
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = element
    }
  }

  useEffect(() => {
    if (autoFocus && internalRef.current) {
      internalRef.current.focus()
    }
  }, [autoFocus])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      console.log('🔍 [ChatInput] Enter pressed:', JSON.stringify({ 
        disabled, 
        isLoading, 
        value: value,
        valueTrimmed: value.trim(),
        valueLength: value.trim().length,
        conditionResult: !disabled && !isLoading && !!value.trim()
      }))
      if (!disabled && !isLoading && value.trim()) {
        console.log('📤 [ChatInput] Calling onSend')
        onSend()
      } else {
        console.log('❌ [ChatInput] Conditions not met:', JSON.stringify({
          disabledCheck: !disabled,
          isLoadingCheck: !isLoading,
          valueCheck: !!value.trim()
        }))
      }
    }
  }

  // 深色主题支持：variant === 'memory' 或 theme === 'dark' 时使用深色样式

  // AgentFlow 风格（浅色）
  if (variant === 'agent' && theme === 'light') {
    return (
      <div className="p-3 bg-white border-t">
        <div className="flex gap-2 items-center bg-slate-50 rounded-xl p-1 border border-slate-200 focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100 transition-all">
          <input
            ref={setRefs}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            className="flex-1 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-slate-400"
          />
          {/* v6.0: 麦克风按钮 */}
          {showVoiceButton && (
            <Button
              size="icon"
              variant="ghost"
              // PTT 模式：只响应长按（mousedown/touchstart），不响应 click
              // 其他模式：响应 click
              onClick={voiceMode !== 'ptt' ? onVoiceClick : undefined}
              onMouseDown={voiceMode === 'ptt' ? onVoiceLongPressStart : undefined}
              onMouseUp={voiceMode === 'ptt' ? onVoiceLongPressEnd : undefined}
              onMouseLeave={voiceMode === 'ptt' && isRecording ? onVoiceLongPressEnd : undefined}
              onTouchStart={voiceMode === 'ptt' ? onVoiceLongPressStart : undefined}
              onTouchEnd={voiceMode === 'ptt' ? onVoiceLongPressEnd : undefined}
              disabled={disabled || isLoading}
              className={`h-9 w-9 rounded-lg transition-all ${
                isRecording 
                  ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                  : voiceMode === 'vad'
                    ? 'bg-green-100 text-green-600 hover:bg-green-200 ring-2 ring-green-300'
                    : voiceMode === 'ptt'
                      ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Mic className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="icon"
            onClick={onSend}
            disabled={!value.trim() || disabled || isLoading}
            className="h-9 w-9 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-all"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2 text-center">
          {showVoiceButton && voiceMode !== 'off' 
            ? voiceMode === 'ptt' ? '长按麦克风说话 · Enter 发送文字' : '🔴 语音监听中 · Enter 发送文字'
            : 'Enter 发送 · Shift+Enter 换行'
          }
        </p>
      </div>
    )
  }

  // MemoryService 风格（深色）
  return (
    <div className="p-4 border-t border-border">
      <div className="flex gap-3 p-2 rounded-2xl bg-card border border-border shadow-sm">
        <input
          ref={setRefs}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-transparent px-3 py-2 outline-none text-foreground placeholder-muted-foreground"
          disabled={disabled || isLoading}
        />
        <button
          onClick={onSend}
          disabled={!value.trim() || disabled || isLoading}
          className="px-5 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-opacity disabled:opacity-50 bg-gradient-to-r from-blue-500 to-purple-600"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              发送
              <Send className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  )
})

ChatInput.displayName = 'ChatInput'

